'use client';
import { imageSource, resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { ProductBadge } from '@eliadmin/core/src/components/product/detail/product-badge';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { Quantity } from './quantity';
import { ProductDetailAvailability } from '@eliadmin/core/src/components/product/detail/product-detail-availability';
import { useRouter } from 'next/navigation';
import Link from '@eliadmin/core/src/components/link';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type ProductCardProps = {
  product: Product;
  imageSizes?: string;
  className?: {
    main?: string;
  };
};

function ProductCardInternal({ product, imageSizes, className }: ProductCardProps) {
  const templateSettings = useGlobalStore((s) => s.templateSettings);
  const filter = useCategoryStore((state) => state.filter);
  const selectedQuantity = useVariantsStore((state) => state.selectedQuantity);
  const router = useRouter();
  const { t } = useTranslations();
  const isWholesaler = useCustomerStore((state) => state.me?.customer.is_wholesaler);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);

  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  });
  const isDiscounted = priceDisplay.showPreviousPrice;

  const hasTags = product?.tags && product?.tags?.length > 0;

  const showBadges = isDiscounted || hasTags;

  return (
    <div
      data-search-navigation
      onClick={(event) => {
        if (
          event.defaultPrevented ||
          (event.target as Element).closest('a,button,input,select,textarea,[role="button"]')
        )
          return;
        router.push(product.slug);
      }}
      className={cn(
        'w-full cursor-pointer rounded-[12px] border',
        'ea-product-card',
        className?.main,
      )}
    >
      <div className="relative flex aspect-square w-full items-start justify-between gap-2 p-3 sm:gap-3 sm:p-4 lg:gap-4">
        {showBadges ? (
          <div className="relative z-10 flex flex-wrap items-start justify-start gap-1">
            {product?.tags?.map((tag) => (
              <ProductBadge
                background={tag?.color ?? ''}
                key={tag?.name}
                color={getContrastYIQ(tag?.color ?? '#FFFFFF')}
                text={tag?.name}
              />
            ))}
            {isDiscounted && (
              <ProductBadge
                color="#9E0505"
                background={'#FFE3E3'}
                text={`${t('text1899')}-${priceDisplay.discount.toFixed(0)}%`}
              />
            )}
          </div>
        ) : (
          <div className="invisible h-4 w-4" />
        )}
        <AddToFavoritesButton
          slug={product.slug}
          buttonSize={'none'}
          buttonVariant={'white'}
          productId={product.id}
          isFavorite={product.is_favorite}
          buttonClassName="relative z-10 min-w-8 max-w-8 min-h-8 max-h-8 sm:min-h-10 sm:max-h-10 sm:min-w-10 sm:max-w-10 lg:max-w-[52px] lg:min-w-[52px] lg:max-h-[52px] lg:min-h-[52px]"
          showText={false}
        />
        <Link
          href={product.slug}
          aria-label={product.name}
          prefetch={false}
          prefetchOnHover
          className="absolute inset-0"
        >
          <Image
            src={imageSource(image) ?? '/images/placeholder.png'}
            alt={resolveImageAlt(image, product?.name)}
            fill
            loading="lazy"
            sizes={imageSizes ?? 'auto, (max-width: 639px) 50vw, (max-width: 1023px) 33vw, 300px'}
            className="absolute inset-0 z-0 h-full w-full object-contain object-center p-2 sm:p-3"
          />
        </Link>
      </div>
      <div className="flex flex-col items-start justify-start gap-3 p-3 sm:gap-4 sm:p-4">
        <Link href={product.slug} prefetch={false} prefetchOnHover className="block w-full">
          <Heading
            title={product.name}
            tag="h3"
            visualTag="h6"
            className={{ title: 'line-clamp-2 h-[38px]' }}
          />
        </Link>
        {(product?.ratingCount ?? 0) > 0 ? (
          <ProductRating rating={product?.rating ?? 0} ratingCount={product?.ratingCount ?? 0} />
        ) : (
          <div className="h-5 w-full bg-transparent" />
        )}
        <ProductDetailAvailability />
        <ProductPrice product={product} />
        <div className="flex w-full flex-col items-start justify-start gap-2 sm:flex-row">
          <Quantity />
          <AddToCartButton
            product={product}
            showIcon={!!templateSettings.product_teaser?.cart_icon}
            sellable={isSellable(product, selectedQuantity)}
            quantity={selectedQuantity}
            customText={t('text1115')}
            className={cn(
              'h-[50px] w-full flex-grow lg:px-4',
              'ea-product-card__add-to-cart-button',
            )}
          />
        </div>
      </div>
    </div>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <VariantsProvider
      state={{ product: props.product, selectedQuantity: props.product.min_quantity ?? 1 }}
    >
      <DialogProvider>
        <ProductCardInternal {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
}

export function ProductCardSkeleton({
  amount,
  variant = 'vertical',
}: {
  amount: number;
  variant?: 'horizontal' | 'vertical';
}) {
  return (
    <>
      {Array.from({ length: amount }).map((_, i) => (
        <div
          key={i}
          className={cn(
            'animate-pulse',
            'col-span-1 w-full',
            variant === 'vertical'
              ? 'flex flex-col items-start justify-start gap-3 sm:gap-4'
              : 'flex items-center justify-start gap-3 pr-2 sm:gap-4 md:pr-3 lg:pr-4',
          )}
        >
          <div
            className={cn(
              'aspect-square w-full rounded-[20px] bg-neutral-50',
              variant === 'horizontal' && 'max-w-[160px] sm:max-w-[170px] lg:max-w-[190px]',
            )}
          />
          <div className={cn('h-[100px] w-full rounded-[20px] bg-neutral-50 sm:h-[113px]')} />
        </div>
      ))}
    </>
  );
}
